"use strict";
import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import classes from "../Assets/Styles/Header.module.css";
import { Button, Divider, Grid, Menu, MenuItem } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";

import { Store } from "../Store";
import AdminRolesNavBar from "../Components/HeaderComponents/AdminRolesNavBar";
import AudiobookRolesNavBar from "../Components/HeaderComponents/AudiobookRolesNavBar";

import { workTypes, roleLabels, languageSpecificRoles } from "../Data/tasksCode";
import {
  convertToDate,
  getLanguageName,
  getTaskNameFormal,
} from "../utils/generalFunctions";
import { primaryColor, getSiteName } from "../utils/constants";
import EmployeRolesNavBar from "../Components/HeaderComponents/EmployeRolesNavBar";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import { clearStoredProjectFile, clearStoredUserInfo, setStoredProjectFile } from "../utils/storage";

const labelColors = "var(--color-topbar-text)";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const siteName = getSiteName(location.pathname);
  useEffect(() => {
    document.title = siteName;
  }, [siteName]);
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo, projectFile, employeeTasks } = state;

  const isCreateProjectPage = /^\/createproject/.test(location.pathname);

  const HIDE_PROJECT_UI_PATHS = [
    /^\/admin\/users$/,          // user list
    /^\/admin\/user\/[^/]+$/,    // user edit
    /^\/admin\/teams$/,          // team list
    /^\/api\//,                  // API pages
    /^\/createproject/,          // project creation flow
  ];

  const shouldShowProjectStrip =
    Object.keys(projectFile).length > 0 &&
    !HIDE_PROJECT_UI_PATHS.some((re) => re.test(location.pathname));


  const signoutHandler = () => {
    ctxDispatch({ type: "USER_SIGNOUT" });
    clearStoredUserInfo();
    clearStoredProjectFile();
    window.location.href = "/";
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [adminAnchorEl, setAdminAnchorEl] = useState(null);
  const openAdmin = Boolean(adminAnchorEl);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWork = (project, role, language) => {
    ctxDispatch({ type: "PROJECT_FILE", payload: project });
    setStoredProjectFile(project);

    if (role === "transcribe") navigate("/work/transcriber");
    else if (role === "translate") navigate(`/work/translator/${language}`);
    else if (role === "performancedirection")
      navigate(`/work/performancedirection/${language}`);
    else if (role === "attribution")
      navigate("/work/attribution");
    else if (role === "lipqualityassurance")
      navigate(`/work/lipqualityassurance/${language}`);
    else if (role === "speakerfaceidentification")
      navigate("/work/speakerfaceidentification");
    else if (role === "qualityassurance")
      navigate(`/work/qualityassurance/${language}`);
  };

  const handleAudiobookWork = (role, language) => {
    if (role === "performancedirection") {
      navigate(`/audiobooks/performancedirection/${projectFile.id}/${language}`);
    } else if (role === "finalqa") {
      navigate(`/audiobooks/finalqa/${projectFile.id}/${language}`);
    }
  };

  const handleNavigate = () => {
    if (userInfo?.isAdmin) {
      ctxDispatch({ type: "PROJECT_FILE_CLEAR" });
      // localStorage.removeItem("projectFile");
      navigate("/adminlanding");
    } else {
      navigate("/landing");
    }
  };

  function transformRoles(roles) {
    return roles
      .map((role) => {
        switch (role) {
          case "transcriber":
            return "transcribe";
          case "translator":
            return "translate";
          case "performancedirection":
          case "lipqualityassurance":
          case "speakerfaceidentification":
          case "qualityassurance":
            return role;
          default:
            return null;
        }
      })
      .filter((role) => role !== null);
  }

  function getRolesByName(data, name) {
    // Check if data is an array
    if (!Array.isArray(data)) {
      console.error("Error: data is not an array");
      return [];
    }

    const project = data?.find((item) => item?.name === name);
    return project ? project?.roles : [];
  }

  const getCurrentAction = () => {
    const segments = location.pathname.split("/");
    let role = "";
    let languageCode = "";

    if (segments[1] === "work") {
      role = segments[2];
      languageCode = segments[3];
    } else if (segments[1] === "audiobooks") {
      role = segments[2];
      languageCode = segments[4];
    } else {
      return "";
    }

    const baseLabel = roleLabels[role] || "";
    if (!baseLabel) {
      return "";
    }

    if (role === "transcribe" || role === "transcriber") {
      const originalCode = projectFile.originalLanguage || projectFile.orginalLang;
      if (originalCode) {
        return `${baseLabel}: ${getLanguageName(originalCode)}`;
      }
      return baseLabel;
    }

    if (role === "translate" || role === "translator") {
      const originalCode = projectFile.originalLanguage || projectFile.orginalLang;
      if (originalCode && languageCode) {
        return `${baseLabel}: ${getLanguageName(originalCode)} to ${getLanguageName(languageCode)}`;
      }
      return baseLabel;
    }

    if (languageSpecificRoles.has(role) && languageCode) {
      return `${baseLabel}: ${getLanguageName(languageCode)}`;
    }

    return baseLabel;
  };

  const currentAction = getCurrentAction();

  return (
    <>
      {userInfo ? (
        <Grid
          item
          container
          className={classes["headerContainer"]}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          id="header"
        >
          <Grid
            item
            xs="auto"
            marginLeft={2}
            onClick={() => handleNavigate()}
            sx={{
              display: "flex",
              cursor: "pointer",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: 1,
            }}
          >
            <img src="/logo-incantor-av-dark.png" alt="logo" className={classes["logo"]} />
          </Grid>
          {shouldShowProjectStrip && (
          <Grid
            item
            sx={{
              position: "absolute",
              left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Grid container direction="column" alignItems="center">
                <Grid item sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {userInfo.isAdmin ? (
                    <>
                      <Grid
                        item
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: primaryColor,
                          margin: "0 8px",
                          minWidth: "32px",
                          justifyContent: "center",
                        }}
                      >
                        {projectFile.hasOwnProperty("audioGenerationStatus") ? (
                          <VolumeUpOutlinedIcon sx={{ fontSize: "var(--font-size-header)" }} />
                        ) : (
                          <MovieOutlinedIcon sx={{ fontSize: "var(--font-size-header)" }} />
                        )}
                      </Grid>
                      <Grid
                        item
                        sx={{
                          color: "var(--color-topbar-text)",
                          fontSize: "var(--font-size-header)",
                          fontWeight: "600",
                          fontFamily: "var(--font-heading)",
                        }}
                      >
                        {projectFile.name}
                      </Grid>
                      <Grid item>
                        {projectFile.hasOwnProperty("audioGenerationStatus") ? (
                          <AudiobookRolesNavBar
                            projectFile={projectFile}
                            handleWork={handleAudiobookWork}
                            getLanguageName={getLanguageName}
                          />
                        ) : (
                          <AdminRolesNavBar
                            workTypes={workTypes}
                            projectFile={projectFile}
                            handleWork={handleWork}
                            getTaskNameFormal={getTaskNameFormal}
                            getLanguageName={getLanguageName}
                            convertToDate={convertToDate}
                          />
                        )}
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid
                        item
                        sx={{
                          color: "var(--color-topbar-text)",
                          fontSize: "var(--font-size-header)",
                          fontWeight: "600",
                          fontFamily: "var(--font-heading)",
                        }}
                      >
                        {projectFile.name}
                      </Grid>
                      <Grid item>
                        <EmployeRolesNavBar
                          workTypes={transformRoles(
                            getRolesByName(employeeTasks, projectFile.name)
                          )}
                          projectFile={projectFile}
                          handleWork={handleWork}
                          getTaskNameFormal={getTaskNameFormal}
                          getLanguageName={getLanguageName}
                          convertToDate={convertToDate}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                {currentAction && (
                  <Grid
                    item
                    sx={{
                      color: "var(--color-topbar-text)",
                      fontSize: "var(--font-size-normal)",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    {currentAction}
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
          {!shouldShowProjectStrip && isCreateProjectPage && (
            <Grid
              item
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Grid
                item
                sx={{
                  color: "var(--color-topbar-text)",
                  fontSize: "var(--font-size-header)",
                  fontWeight: "600",
                  fontFamily: "var(--font-heading)",
                }}
              >
                Create Project
              </Grid>
            </Grid>
          )}
          <Grid
            item
            xs="auto"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flexWrap: "nowrap",
              gap: 2,
            }}
          >
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              <PersonIcon className={classes["icons"]} fontSize="medium" />
              <Button
                onClick={handleClick}
                style={{
                  color: `${labelColors}`,
                  textTransform: "none",
                  fontSize: "var(--font-size-normal)",
                }}
              >
                {userInfo.name} <ArrowDropDownIcon />
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  style: {
                    width: 144,
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleClose();
                    navigate("/userprofile");
                  }}
                >
                  Profile
                </MenuItem>
                <MenuItem onClick={signoutHandler}>
                  Sign out
                </MenuItem>
              </Menu>
            </Grid>

            {userInfo.isAdmin && (
              <Grid item sx={{ display: "flex", alignItems: "center" }}>
                <SettingsIcon
                  className={classes["icons"]}
                  fontSize="medium"
                />
                <Button
                  onClick={(event) => {
                    setAdminAnchorEl(event.currentTarget);
                  }}
                  style={{
                    color: `${labelColors}`,
                    textTransform: "none",
                    fontSize: "var(--font-size-normal)",
                  }}
                >
                  Admin <ArrowDropDownIcon />
                </Button>
                <Menu
                  anchorEl={adminAnchorEl}
                  open={openAdmin}
                  onClose={() => setAdminAnchorEl(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  MenuListProps={{
                    style: {
                      width: 200,
                    },
                  }}
                >
                  <MenuItem onClick={() => setAdminAnchorEl(null)}>
                    <Link
                        style={{
                            textDecoration: "none",
                            color: "inherit",
                        }}
                        to="/createproject?type=video"
                    >
                        New Video Project
                    </Link>
                  </MenuItem>
                  <MenuItem onClick={() => setAdminAnchorEl(null)}>
                    <Link
                        style={{
                            textDecoration: "none",
                            color: "inherit",
                        }}
                        to="/createproject?type=audiobook"
                    >
                        New Audio Project
                    </Link>
                  </MenuItem>
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem onClick={() => setAdminAnchorEl(null)}>
                    <Link
                        style={{
                            textDecoration: "none",
                            color: "inherit",
                        }}
                        to="/admin/videoprojects"
                    >
                        Video Projects
                    </Link>
                  </MenuItem>
                  <MenuItem onClick={() => setAdminAnchorEl(null)}>
                    <Link
                        style={{
                            textDecoration: "none",
                            color: "inherit",
                        }}
                        to="/admin/audioprojects"
                    >
                        Audio Projects
                    </Link>
                  </MenuItem>
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem onClick={() => setAdminAnchorEl(null)}>
                    <Link
                        style={{
                            textDecoration: "none",
                            color: "inherit",
                        }}
                        to="/admin/users"
                    >
                        Users
                    </Link>
                  </MenuItem>
                  <MenuItem onClick={() => setAdminAnchorEl(null)}>
                    <Link
                        style={{
                            textDecoration: "none",
                            color: "inherit",
                        }}
                        to="/admin/teams"
                    >
                        Teams
                    </Link>
                  </MenuItem>
                </Menu>
              </Grid>
            )}
          </Grid>
        </Grid >
      ) : (
        <Grid
          item
          container
          className={classes["headerContainer"]}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          id="header"
        >
          <Grid
            item
            xs="auto"
            marginLeft={2}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "nowrap",
            }}
          >
            <img src="/logo-incantor-av-dark.png" alt="logo" className={classes["logo"]} />
          </Grid>
          {/* <Grid item>
            <Grid
              item
              container
              alignItems="center"
              direction="row"
              // justifyContent="space-between"
            >
              <Grid item marginRight={5}>
                <HelpIcon fontSize="large" className={classes["icons"]} />
              </Grid>
              <Grid item marginRight={5}>
                <CircleNotificationsIcon
                  fontSize="large"
                  className={classes["icons"]}
                />
              </Grid>
            </Grid>
          </Grid> */}
        </Grid>
      )}
    </>
  );
};

export default Header;
